/**
 * Clients — the FPS platform level, styled to docs/prototype.html.
 *
 * FPS staff see every client, create them, and edit any. A client admin sees
 * only their own and can edit its branding, so this doubles as their entry
 * point rather than needing a separate screen.
 *
 * The card is not itself a link. It holds two actions — Edit and Open
 * workspace — and nesting a button inside an anchor is both invalid markup and
 * ambiguous to anyone using a keyboard or screen reader.
 */

import { t } from '../lib/copy';
import { T } from '../lib/copyEdit.tsx';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../lib/auth.ts';

type Approval = 'fps' | 'client' | 'co_review';

interface Client {
  id: string;
  name: string;
  /** Set when the client has been taken out of circulation: Front Porch keeps
   *  access, their own admins and sponsors lose it. Null for a live client. */
  archivedAt?: string | null;
  website: string | null;
  appIconData: string | null;
  /** Whether a real client LOGO has been uploaded. Distinct from appIconData,
   *  which is a 32px favicon fetched from Google and is not the logo. */
  hasLogo: boolean;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string | null;
  brandFont: string;
  emailFromName: string;
  replyToEmail: string;
  emailSignature: string | null;
  /** Null means "follow the platform default". */
  reminderSendHour: number | null;
  reminderSkipWeekends: boolean | null;
  approvalResponsibility: Approval;
  archiveAfterDays: number;
  counts?: {
    events: number;
    sponsors: number;
    admins: number;
    /** Events currently collecting. A draft is not collecting and a complete
     *  or archived one is finished, so neither is work. */
    activeEvents: number;
    /** Sponsors on active events with something awaiting a decision. */
    pendingReview: number;
    /** Sponsors on active events who have sent nothing at all. */
    outstanding: number;
  };
}

/** Full sentences. These were previously truncated to their first word to fit
 *  a numeric stat slot, which produced "Front" and told nobody anything. */
const APPROVAL_LABEL: Record<Approval, string> = {
  fps: 'Front Porch reviews and approves',
  client: 'Client reviews and approves',
  co_review: 'Co-review, either side can approve',
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export default function Clients() {
  const { data: session } = useSession();
  const isPlatformAdmin = (session?.user as { role?: string } | undefined)?.role === 'fps_super_admin';

  const [clients, setClients] = useState<Client[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  /** Which client a confirmation is open for, or null. */
  const [archiving, setArchiving] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState<Client | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/clients');
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { clients: Client[] };
      setClients(body.clients);
    } catch {
      setError('Could not load clients.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * OPENED FROM THE CLIENT'S OWN PAGE, via ?edit=, ?archive= or ?delete=.
   *
   * The forms and dialogs live here because this is where the one client form
   * already is, and a second copy of fourteen fields would be a second copy to
   * keep right. The menu on a client's Dashboard navigates here with the id,
   * which is also what makes the action bookmarkable and survive a reload.
   */
  useEffect(() => {
    if (!clients) return;
    const params = new URLSearchParams(window.location.search);
    const find = (key: string) => {
      const id = params.get(key);
      return id ? (clients.find((c) => c.id === id) ?? null) : null;
    };

    const toEdit = find('edit');
    const toArchive = find('archive');
    const toDelete = find('delete');

    if (toEdit) setEditing(toEdit);
    if (toArchive) setArchiving(toArchive);
    if (toDelete) setDeleting(toDelete);

    if (toEdit || toArchive || toDelete) {
      /* The query is consumed once. Left in place, a reload would reopen a
         dialog somebody had already dismissed. */
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [clients]);

  /**
   * Archives a client, or brings one back.
   *
   * Restoring has no confirmation: it undoes something rather than destroying
   * anything, and archiving again is one click away.
   */
  async function setArchived(client: Client, archived: boolean) {
    setError(null);
    try {
      const res = await fetch(`/api/clients/${client.id}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'That did not save.');
        return;
      }
      setArchiving(null);
      void load();
    } catch {
      setError('That did not save. Check your connection and try again.');
    }
  }

  if (error) return <p className="auth-error">{error}</p>;
  if (!clients) return <div className="app-loading" aria-busy="true" />;

  const formOpen = creating || editing !== null;

  /* Summed here rather than served as a platform total, because the list is
     already scoped to what this caller may see: a client admin assigned to one
     client gets their one client's numbers, and a super admin gets everyone's,
     from the same arithmetic. A separate total would need its own scoping and
     would be a second place for it to be wrong. */
  const totals = clients.reduce(
    (acc, c) => ({
      activeEvents: acc.activeEvents + (c.counts?.activeEvents ?? 0),
      pendingReview: acc.pendingReview + (c.counts?.pendingReview ?? 0),
      outstanding: acc.outstanding + (c.counts?.outstanding ?? 0),
    }),
    { activeEvents: 0, pendingReview: 0, outstanding: 0 },
  );

  return (
    <>
      {archiving && (
        <ClientSheet onClose={() => setArchiving(null)}>
          <div className="panel">
            <div className="panel-head"><h3>Archive {archiving.name}</h3></div>
            <div className="panel-body">
              <p className="fdesc" style={{ marginTop: 0 }}>
                <T
                  k="clients.archive.intro"
                  d="Nothing is deleted. Every event, sponsor and logo stays exactly where it is."
                />
              </p>
              <ul className="willlist">
                <li><T k="clients.archive.w1" d="Front Porch can still open it and read everything" /></li>
                <li><T k="clients.archive.w2" d="Their own administrators lose access" /></li>
                <li><T k="clients.archive.w3" d="Their sponsors' upload links stop working" /></li>
                <li><T k="clients.archive.w4" d="Every reminder under them stops" /></li>
                <li><T k="clients.archive.w5" d="Restoring brings all of it back, including the links" /></li>
              </ul>
              <div className="factions">
                <button type="button" className="btn-compact btn-compact--quiet" onClick={() => setArchiving(null)}>
                  <T k="common.action.cancel" d="Cancel" />
                </button>
                <button type="button" className="btn-compact" onClick={() => void setArchived(archiving, true)}>
                  <T k="clients.archive.confirm" d="Archive Client" />
                </button>
              </div>
            </div>
          </div>
        </ClientSheet>
      )}

      {deleting && (
        <DeleteClientPanel
          client={deleting}
          onCancel={() => setDeleting(null)}
          onDone={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}

      <div className="phead">
        <div>
          <p className="eyebrow">{isPlatformAdmin ? t('clients.eyebrow.platform', 'Front Porch Solutions') : t('clients.eyebrow.client', 'Your Organizations')}</p>
          <h1 className="title"><T k="clients.title" d="Clients" /></h1>
          <p className="lede">
            {isPlatformAdmin
              ? t('clients.lede.platform', 'Every organization Front Porch manages on the platform.')
              /* Says what the page is FOR rather than what the list is. The
                 reader here has been given access to somebody else's
                 organizations, which "shared with your account" states and
                 "the organizations you administer" rather overclaims. */
              : t('clients.lede.client', 'View and manage organizations shared with your account.')}
          </p>
        </div>
        {isPlatformAdmin && !formOpen && (
          <button type="button" className="btn-compact" onClick={() => setCreating(true)}>
            {/* The plus the prototype has on every create action. It is the
                thing that distinguishes "make a new one" from "go somewhere",
                which matters most on a page whose other controls navigate. */}
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Create Client
          </button>
        )}
      </div>

      {/* THE PLATFORM AT A GLANCE, which the page did not have. It opened
          straight onto a grid of cards, so "is anything waiting on us" could
          only be answered by opening each client in turn.

          Hidden while the form is open and when there are no clients: four
          zeroes above an empty state is furniture, not information. */}
      {!formOpen && clients.length > 0 && (
        <div className="stats">
          <div className="stat">
            <div className="label"><T k="clients.stat.clients" d="Clients" /></div>
            <div className="n">{clients.length}</div>
            <div className="m"><T k="clients.stat.clientsSub" d="Active on platform" /></div>
          </div>
          <div className="stat">
            <div className="label"><T k="clients.stat.activeEvents" d="Active events" /></div>
            <div className="n">{totals.activeEvents}</div>
            <div className="m"><T k="clients.stat.activeEventsSub" d="Across all clients" /></div>
          </div>
          <div className="stat">
            <div className="label"><T k="stat.pendingReview" d="Pending review" /></div>
            <div className="n">{totals.pendingReview}</div>
            <div className="m"><T k="stat.pendingReviewSub" d="Awaiting a decision" /></div>
          </div>
          <div className="stat">
            <div className="label"><T k="stat.outstanding" d="Outstanding" /></div>
            <div className="n">{totals.outstanding}</div>
            <div className="m"><T k="clients.stat.outstandingSub" d="Sponsors yet to upload" /></div>
          </div>
        </div>
      )}

      {/* The form replaces the list rather than stacking above it. */}
      {formOpen ? (
        <ClientForm
          client={editing}
          isPlatformAdmin={isPlatformAdmin}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void load();
          }}
        />
      ) : clients.length === 0 ? (
        <div className="empty">
          <p className="empty-title"><T k="clients.empty.title" d="No clients yet" /></p>
          <p className="empty-copy">
            {isPlatformAdmin
              ? t('clients.empty.platform', 'Create the first client to set up their brand and start collecting sponsor logos.')
              : t('clients.empty.client', 'You have not been assigned to a client yet. Ask Front Porch to add you.')}
          </p>
        </div>
      ) : (
        <ul className="cgrid">
          {clients.map((c) => (
            /* The whole card is the target now. The footer link is what
               stretches to cover it, so there is still exactly one accessible
               name for the destination rather than a name per clickable
               region. See .ccard .clink::after. */
            <li key={c.id} className="ccard">
              <div className="ch">
                {/* THE FETCHED ICON, NOT THE UPLOADED LOGO, and that is a
                    decision rather than an oversight. The uploaded logo is the
                    client's brand as their SPONSORS should see it: it leads the
                    portal and every sponsor email, and that is the whole of its
                    job. This is an internal index of organisations, where a
                    small square icon identifies a row at a glance and a wide
                    horizontal lockup would shrink to an illegible smear in a
                    42px tile.

                    White ground when there is an icon. Favicons are usually
                    transparent and the brand colour was suggested FROM that
                    icon's own colours, so a coloured tile reliably paints the
                    mark in its own colour on itself. */}
                {c.appIconData ? (
                  <span className="clogo clogo--icon">
                    <img src={c.appIconData} alt="" />
                  </span>
                ) : (
                  <span className="clogo" style={{ background: c.primaryColor }}>
                    {initials(c.name)}
                  </span>
                )}
                <div className="ch-text">
                  <h3>{c.name}</h3>
                  <div className="csub">{c.website ?? c.replyToEmail}</div>
                </div>
                {c.archivedAt && (
                  <span className="badge b-neutral" style={{ marginRight: 'auto' }}>
                    <span className="d" />
                    <T k="clients.archived" d="Archived" />
                  </span>
                )}
                <button type="button" className="cedit" onClick={() => { setCreating(false); setEditing(c); }}>
                  Edit
                </button>
{/* FRONT PORCH ONLY. Archiving a client is a decision about the
                    relationship, not a setting inside it, and the server
                    answers a client administrator with a 404. This page is
                    reachable by them — it lists the organizations they belong
                    to — so without this guard they would see an Archive button
                    on their own card and get "Not found" when they pressed it.
                    Edit above is deliberately not gated: a client admin may
                    edit their own client, and PATCH allows exactly that. */}
                {isPlatformAdmin &&
                  (c.archivedAt ? (
                    <button type="button" className="cedit" onClick={() => void setArchived(c, false)}>
                      <T k="clients.action.restore" d="Restore" />
                    </button>
                  ) : (
                    <button type="button" className="cedit" onClick={() => setArchiving(c)}>
                      <T k="clients.action.archive" d="Archive" />
                    </button>
                  ))}
              </div>

              {/* Counts belong in the stat row. A setting does not, which is
                  what produced "Front" here previously. */}
              <div className="cstats">
                <div className="cstat">
                  <b>{c.counts?.events ?? 0}</b>
                  <span><T k="clients.card.events" d="Events" /></span>
                </div>
                <div className="cstat">
                  <b>{c.counts?.sponsors ?? 0}</b>
                  <span><T k="clients.card.sponsors" d="Sponsors" /></span>
                </div>
                <div className="cstat">
                  <b>{c.counts?.admins ?? 0}</b>
                  <span><T k="clients.card.admins" d="Admins" /></span>
                </div>
              </div>

              <div className="cfoot">
                <span className="capproval">{APPROVAL_LABEL[c.approvalResponsibility]}</span>
                <Link className="clink" to={`/c/${c.id}`}>
                  Open workspace
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

interface Suggestion {
  iconDataUri: string | null;
  colors: string[];
}

/**
 * One form for both create and edit.
 *
 * Keeping them together means a field added to one cannot be forgotten in the
 * other, which is how create and edit forms drift apart.
 */
function ClientForm({
  client,
  isPlatformAdmin,
  onCancel,
  onSaved,
}: {
  client: Client | null;
  isPlatformAdmin: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = client !== null;

  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const [website, setWebsite] = useState(client?.website ?? '');
  const [primary, setPrimary] = useState(client?.primaryColor ?? '#0E193D');
  const [secondary, setSecondary] = useState(client?.secondaryColor ?? '#64748B');
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [fetching, setFetching] = useState(false);
  /**
   * The client's logo.
   *
   * HELD SEPARATELY FROM THE FORM because it is a file, not a field: it goes
   * up as multipart to its own endpoint, through the same ingest path as every
   * other upload. On a NEW client there is no client id to store it against
   * until the row exists, so it is held here and sent immediately after the
   * create succeeds.
   */
  /**
   * The sender name, tracked so it can follow the client name until somebody
   * types their own. `senderEdited` is what stops it overwriting a deliberate
   * choice the moment the client name is corrected.
   */
  const [senderName, setSenderName] = useState(client?.emailFromName ?? '');
  const [senderEdited, setSenderEdited] = useState(Boolean(client?.emailFromName));
  /** Empty string means "follow the platform default", which is what null on
   *  the row means. Kept as a string because that is what a <select> gives. */
  const [sendHour, setSendHour] = useState(
    client?.reminderSendHour === null || client?.reminderSendHour === undefined
      ? ''
      : String(client.reminderSendHour),
  );
  const [skipWeekends, setSkipWeekends] = useState(
    client?.reminderSkipWeekends === null || client?.reminderSkipWeekends === undefined
      ? ''
      : String(client.reminderSkipWeekends),
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const logoInput = useRef<HTMLInputElement>(null);

  /**
   * Pulls the app icon and suggests colours from the website domain.
   *
   * The server never contacts that host — the icon comes from Google's favicon
   * service and the colours are derived from it (SEC-10). Results are an
   * explicit guess: both swatches stay editable and the hint says so.
   */
  async function fetchBrand() {
    if (!website.trim()) return;
    setFetching(true);
    setProblem(null);

    const res = await fetch('/api/clients/brand-suggestion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain: website.trim() }),
    });

    setFetching(false);
    if (!res.ok) {
      setProblem('That does not look like a website address.');
      return;
    }

    const body = (await res.json()) as Suggestion;
    setSuggestion(body);
    if (body.colors[0]) setPrimary(body.colors[0]);
    if (body.colors[1]) setSecondary(body.colors[1]);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setProblem(null);

    const form = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {
      name: String(form.get('name') ?? '').trim(),
      website: website.trim() || undefined,
      emailFromName: String(form.get('emailFromName') ?? '').trim(),
      replyToEmail: String(form.get('replyToEmail') ?? '').trim(),
      brandFont: String(form.get('brandFont') ?? 'Georgia'),
      primaryColor: primary,
      secondaryColor: secondary,
      emailSignature: String(form.get('emailSignature') ?? '').trim() || null,
      /* Null rather than absent, so choosing "Platform default" CLEARS an
         override rather than leaving the previous one in place. */
      reminderSendHour: sendHour === '' ? null : Number(sendHour),
      reminderSkipWeekends: skipWeekends === '' ? null : skipWeekends === 'true',
    };

    /* The fetched icon, which was previously shown in the preview and then
     * discarded on save — the form never sent it and the API never accepted
     * it, so appIconData was null for every client. Only sent when a fetch
     * produced one, so editing a client without re-fetching keeps the icon it
     * already has rather than clearing it. */
    if (suggestion?.iconDataUri) {
      payload.appIconData = suggestion.iconDataUri;
    }

    // Only staff can set these, and only staff are shown the controls.
    if (isPlatformAdmin) {
      payload.approvalResponsibility = String(form.get('approvalResponsibility') ?? 'fps');
      payload.archiveAfterDays = Number(form.get('archiveAfterDays') ?? 30);
    }

    const res = await fetch(isEdit ? `/api/clients/${client.id}` : '/api/clients', {
      method: isEdit ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      setSaving(false);
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setProblem(body.error ?? 'That did not save. Check the fields and try again.');
      return;
    }

    /* The logo, after the row exists. A create has no client id to store
       against until this point, and an edit may be replacing or clearing one.
       A failure here does not undo the save: the client is real, and the
       message says which half did not land. */
    const saved = (await res.json().catch(() => ({}))) as { client?: { id: string } };
    const id = isEdit ? client.id : saved.client?.id;

    if (id && (logoFile || logoRemoved)) {
      const logoRes = logoFile
        ? await fetch(`/api/clients/${id}/logo`, {
            method: 'POST',
            body: (() => {
              const data = new FormData();
              data.append('file', logoFile);
              return data;
            })(),
          })
        : await fetch(`/api/clients/${id}/logo`, { method: 'DELETE' });

      if (!logoRes.ok) {
        setSaving(false);
        const body = (await logoRes.json().catch(() => ({}))) as { error?: string };
        setProblem(
          `${client?.name ?? 'The client'} was saved, but the logo did not upload. ${
            body.error ?? 'Please try again.'
          }`,
        );
        return;
      }
    }

    setSaving(false);
    onSaved();
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="panel-head">
        <h3>{isEdit ? `Edit ${client.name}` : 'New client'}</h3>
        <span className="hint">branding applies to sponsor emails &amp; portal only</span>
      </div>

      <div className="panel-body">
        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="name"><T k="clients.field.name" d="Client Name" /></label>
            <input
              id="name"
              name="name"
              required
              maxLength={200}
              className="fi"
              placeholder="Front Porch Solutions"
              defaultValue={client?.name ?? ''}
              onChange={(e) => {
                /* Only until somebody types their own. Overwriting a
                   deliberate sender name because the client name was
                   corrected would be worse than the empty field this
                   replaces. */
                if (senderEdited) return;
                const named = e.target.value.trim();
                /* Empty in, empty out. Without the guard, clearing the client
                   name leaves the sender reading a bare "Sponsorships". */
                setSenderName(named ? `${named} Sponsorships` : '');
              }}
            />
          </div>

          <div className="fg">
            <label className="fl" htmlFor="website"><T k="common.field.website" d="Website" /></label>
            <div className="fetch-row">
              <input
                id="website"
                className="fi"
                placeholder="frontporchsolutions.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
              <button
                type="button"
                className="btn-compact btn-compact--quiet"
                onClick={fetchBrand}
                disabled={fetching}
              >
                {fetching ? 'Fetching…' : 'Fetch Brand'}
              </button>
            </div>
            <p className="fdesc"><T k="clients.field.websiteHint" d="Pulls the app icon and suggests brand colors. All editable." /></p>
          </div>
        </div>

        {/* THE CLIENT LOGO, which is not the app icon above and is the reason
            the reviewer could see artwork on the client cards and find no
            field to set any. That artwork is a 32px favicon from Google; this
            is the association's actual logo, and it is what the sponsor portal
            and every sponsor email lead with. */}
        <div className="fg">
          <label className="fl" htmlFor="clientLogo"><T k="clients.field.logo" d="Client Logo" /></label>
          <div className="logo-field">
            <span className="logo-preview">
              {logoFile ? (
                <img src={URL.createObjectURL(logoFile)} alt="" />
              ) : client?.logoUrl && !logoRemoved ? (
                <img src={client.logoUrl} alt="" />
              ) : (
                <span className="logo-preview-none"><T k="clients.field.logoNone" d="None" /></span>
              )}
            </span>
            <div className="logo-actions">
              <input
                ref={logoInput}
                id="clientLogo"
                type="file"
                className="portal-file"
                accept=".svg,.eps,.ai,.pdf,.png,.jpg,.jpeg,image/svg+xml,application/pdf,application/postscript,image/png,image/jpeg"
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) {
                    setLogoFile(chosen);
                    setLogoRemoved(false);
                  }
                }}
              />
              <button
                type="button"
                className="btn-compact btn-compact--quiet"
                onClick={() => logoInput.current?.click()}
              >
                {/* `logoRemoved` has to be in here. Without it the button went
                    on reading "Replace Logo" after Remove had emptied the
                    preview, offering to replace a logo that was no longer
                    shown. QA, 8 September 2026. */}
                {(client?.hasLogo && !logoRemoved) || logoFile ? 'Replace Logo' : 'Upload Logo'}
              </button>
              {(logoFile || (client?.hasLogo && !logoRemoved)) && (
                <button
                  type="button"
                  className="btn-compact btn-compact--quiet"
                  onClick={() => {
                    setLogoFile(null);
                    setLogoRemoved(Boolean(client?.hasLogo));
                    if (logoInput.current) logoInput.current.value = '';
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <p className="fdesc">
            Shown at the top of sponsor emails and on the sponsor upload page. Vector is best.
            Without one, those fall back to the client name as text.
          </p>
        </div>

        {suggestion && (
          <div className="fg brand-preview">
            <span className="clogo" style={{ background: suggestion.iconDataUri ? '#fff' : primary }}>
              {suggestion.iconDataUri ? <img src={suggestion.iconDataUri} alt="" /> : '?'}
            </span>
            <p className="fdesc">
              Suggested from <b>{website.trim()}</b>. This is a best guess from their icon, so
              adjust anything that looks off.
            </p>
          </div>
        )}

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="primaryColor"><T k="clients.field.primaryColor" d="Primary Color" /></label>
            <div className="colorf">
              <input
                id="primaryColor"
                type="color"
                value={primary}
                onChange={(e) => setPrimary(e.target.value.toUpperCase())}
              />
              <input
                className="fi hex"
                value={primary}
                onChange={(e) => setPrimary(e.target.value.toUpperCase())}
                aria-label="Primary color hex"
              />
            </div>
          </div>

          <div className="fg">
            <label className="fl" htmlFor="secondaryColor"><T k="clients.field.secondaryColor" d="Secondary Color" /></label>
            <div className="colorf">
              <input
                id="secondaryColor"
                type="color"
                value={secondary}
                onChange={(e) => setSecondary(e.target.value.toUpperCase())}
              />
              <input
                className="fi hex"
                value={secondary}
                onChange={(e) => setSecondary(e.target.value.toUpperCase())}
                aria-label="Secondary color hex"
              />
            </div>
          </div>
        </div>

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="emailFromName"><T k="clients.field.fromName" d="Email Sender Name" /></label>
            <input
              id="emailFromName"
              name="emailFromName"
              required
              className="fi"
              /* PREFILLED, NOT PLACEHOLDERED. The field was empty on a new
                 client with grey placeholder text reading "Front Porch
                 Conference Sponsorships", which looks like a value and submits
                 nothing. It is the likeliest reason somebody reports that the
                 sender name is not populating: it looked filled in and was
                 not. It follows the client name until it is edited, and the
                 suggestion is a real value in the field. */
              value={senderName}
              onChange={(e) => {
                setSenderName(e.target.value);
                setSenderEdited(true);
              }}
            />
            <p className="fdesc">
              What sponsors see in their inbox. Suggested from the client name; change it if they
              use something else.
            </p>
          </div>
          <div className="fg">
            <label className="fl" htmlFor="replyToEmail"><T k="clients.field.replyTo" d="Reply-To Address" /></label>
            <input
              id="replyToEmail"
              name="replyToEmail"
              type="email"
              required
              className="fi"
              placeholder="info@frontporchsolutions.com"
              defaultValue={client?.replyToEmail ?? ''}
            />
            <p className="fdesc"><T k="clients.field.replyToHint" d="Where sponsor replies go." /></p>
          </div>
        </div>

        {/* T23: reminder timing, per client. It was global, in a settings
            table only an FPS super admin can write, which meant one
            association's preference applied to the other. Both fields fall
            back to that platform default when left alone, so a client who has
            no opinion still follows one setting that can be changed in one
            place. The DAY OFFSETS stay fixed: 21, 7, 3, 1 and 0 days. */}
        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="reminderSendHour"><T k="clients.field.sendHour" d="Reminder Send Time" /></label>
            <select
              id="reminderSendHour"
              className="fi"
              value={sendHour}
              onChange={(e) => setSendHour(e.target.value)}
            >
              <option value="">Platform default</option>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={String(h)}>
                  {`${String(h).padStart(2, '0')}:00`}
                </option>
              ))}
            </select>
            <p className="fdesc">In each event's own timezone, not this client's.</p>
          </div>

          <div className="fg">
            <label className="fl" htmlFor="reminderSkipWeekends"><T k="clients.field.weekends" d="Weekend Reminders" /></label>
            <select
              id="reminderSkipWeekends"
              className="fi"
              value={skipWeekends}
              onChange={(e) => setSkipWeekends(e.target.value)}
            >
              <option value="">Platform default</option>
              <option value="true">Skip weekends</option>
              <option value="false">Send any day</option>
            </select>
            <p className="fdesc">
              Reminder days are fixed at 21, 7, 3 and 1 days before the deadline, and the day
              itself.
            </p>
          </div>
        </div>

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="brandFont"><T k="clients.field.font" d="Brand Font" /></label>
            <select
              id="brandFont"
              name="brandFont"
              className="fi"
              defaultValue={client?.brandFont ?? 'Georgia'}
            >
              <option>Georgia</option>
              <option>System sans (Arial / Helvetica)</option>
            </select>
            <p className="fdesc"><T k="clients.field.fontHint" d="Sponsor portal and emails only, never this app." /></p>
          </div>
          <div className="fg">
            <label className="fl" htmlFor="emailSignature"><T k="clients.field.signature" d="Email Signature" /></label>
            <textarea
              id="emailSignature"
              name="emailSignature"
              className="fi ft"
              rows={2}
              defaultValue={client?.emailSignature ?? ''}
            />
          </div>
        </div>

        {isPlatformAdmin && (
          <div className="frow2">
            <div className="fg">
              <label className="fl" htmlFor="approvalResponsibility"><T k="clients.field.approval" d="Approval Responsibility" /></label>
              <select
                id="approvalResponsibility"
                name="approvalResponsibility"
                className="fi"
                defaultValue={client?.approvalResponsibility ?? 'fps'}
              >
                <option value="fps">Front Porch reviews and approves</option>
                <option value="client">Client reviews and approves</option>
                <option value="co_review">Co-review, either side can approve</option>
              </select>
              <p className="fdesc">
                Sets who gets the "needs review" alerts. Both roles can always approve; this is the
                default owner.
              </p>
            </div>
            <div className="fg">
              <label className="fl" htmlFor="archiveAfterDays">Auto-Archive After</label>
              <select
                id="archiveAfterDays"
                name="archiveAfterDays"
                className="fi"
                defaultValue={String(client?.archiveAfterDays ?? 30)}
              >
                <option value="0">Never</option>
                <option value="30">30 days after the event</option>
                <option value="60">60 days after the event</option>
                <option value="90">90 days after the event</option>
              </select>
              <p className="fdesc">Counted from the event date, not from completion.</p>
            </div>
          </div>
        )}

        {problem && <p className="auth-error">{problem}</p>}

        <div className="factions">
          <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-compact" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Client'}
          </button>
        </div>
      </div>
    </form>
  );
}

/** A panel over the page. Same shape as the sponsors screen's, for the same
 *  reason: a confirmation rendered inline above a long list is one nobody sees. */
function ClientSheet({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="sheet-wrap" role="dialog" aria-modal="true">
      <button type="button" className="sheet-scrim" aria-label="Close" onClick={onClose} />
      <div className="sheet">{children}</div>
    </div>
  );
}

/**
 * Deleting a client.
 *
 * THE LARGEST DELETE IN THE PRODUCT, and the dialog's job is to say so before
 * anybody is near the button. Twelve tables cascade from a client row, so it
 * asks the server first and shows what is actually there rather than a generic
 * warning.
 *
 * It also says plainly that archiving is usually what was meant, because this
 * is precisely where somebody is about to destroy the record of four years of
 * conferences in order to tidy up a list.
 */
function DeleteClientPanel({
  client,
  onCancel,
  onDone,
}: {
  client: Client;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [confirmName, setConfirmName] = useState('');
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [impact, setImpact] = useState<{
    events: number;
    sponsors: number;
    logos: number;
    emails: number;
  } | null>(null);
  const [needsName, setNeedsName] = useState(false);

  async function remove() {
    setWorking(true);
    setProblem(null);
    try {
      const res = await fetch(`/api/clients/${client.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(needsName ? { confirmName } : {}),
      });

      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          impact?: { events: number; sponsors: number; logos: number; emails: number };
        };
        setImpact(body.impact ?? null);
        setNeedsName(true);
        return;
      }

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setProblem(body.error ?? 'That could not be deleted.');
        return;
      }
      onDone();
    } catch {
      setProblem('That could not be deleted. Check your connection and try again.');
    } finally {
      setWorking(false);
    }
  }

  /* Asked for as the panel opens, so somebody sees the scale before reaching
     for the button rather than after. The server refuses and reports. */
  useEffect(() => {
    void remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once.
  }, []);

  return (
    <ClientSheet onClose={onCancel}>
      <div className="panel">
        <div className="panel-head"><h3>Delete {client.name}</h3></div>
        <div className="panel-body">
          {needsName ? (
            <>
              <p className="auth-error" style={{ marginTop: 0 }}>
                <T k="clients.delete.permanent" d="This cannot be undone, and it is not a small delete." />
              </p>
              <p className="fdesc">
                <T k="clients.delete.intro" d="Everything recorded under this client goes with it:" />
              </p>
              <ul className="willlist">
                <li><b>{impact?.events ?? 0}</b> <T k="clients.delete.events" d="events" /></li>
                <li><b>{impact?.sponsors ?? 0}</b> <T k="clients.delete.sponsors" d="sponsors, and every contact on them" /></li>
                <li><b>{impact?.logos ?? 0}</b> <T k="clients.delete.logos" d="uploaded logos, and the decisions made on them" /></li>
                <li><b>{impact?.emails ?? 0}</b> <T k="clients.delete.emails" d="emails of history, and every sponsor timeline" /></li>
              </ul>
              <p className="fdesc">
                <T
                  k="clients.delete.prefer"
                  d="If you have simply stopped working with them, archive instead. That keeps all of it and still closes them out."
                />
              </p>

              <div className="fg">
                <label className="fl" htmlFor="confirm-client">Type {client.name} to confirm</label>
                <input
                  id="confirm-client"
                  className="fi"
                  placeholder={client.name}
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                />
              </div>
            </>
          ) : (
            <p className="fdesc" style={{ marginTop: 0 }}>
              <T k="clients.delete.checking" d="Checking what this would remove…" />
            </p>
          )}

          {problem && <p className="auth-error">{problem}</p>}

          <div className="factions">
            <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
              <T k="common.action.cancel" d="Cancel" />
            </button>
            <button
              type="button"
              className="btn-compact btn-compact--danger"
              disabled={working || (needsName && confirmName !== client.name)}
              onClick={() => void remove()}
            >
              {working ? 'Deleting…' : 'Delete Permanently'}
            </button>
          </div>
        </div>
      </div>
    </ClientSheet>
  );
}
